import React, { Fragment, useCallback, useState } from 'react';
import styled from 'styled-components';

import {
  calculatePagePercentageAdjustments,
  getPercentageAdjustedValue,
  getPageFieldPositions,
} from '../../lib/documentHelper';

import FieldModes from '../../constants/FieldModes';

import GridLayout from './GridLayout';
import FieldCreationDropLayer from './FieldCreationDropLayer';
import PagePositionContext from '../../context/PagePositionContext';

const StyledWrapper = styled.div`
  width: ${(props) => props.width}px;
  height: auto;
  overflow-y: auto;
  overflow-x: hidden;
`;

const StyledBorderWrapper = styled.div`
  display: flex;
  flex: 1;
  border: ${(props) => props.border};
  padding: ${(props) => props.padding || 0}px;
  margin: ${(props) => props.margin || 0}px;
  min-height: ${(props) => props.height}px;
`;

const StyledPageFields = styled.div`
  display: flex;
  flex: 1;
`;

const FormPage = (props) => {

  const {

    mode,

    //Container properties
    containerWidth = 816,
    containerHeight = 1056,

    //Page Properties
    width = 816,
    height = 1056,
    cols,
    rowHeight,
    presentation,
    margin,
    padding,
    borderWidth,
    borderColor,
    borderStyle,

    fields,

    onFieldCreate,

    enableVirtualFields = true,
    enableLazyLoadFields = false,

    mockTestDndMonitor,

  } = props;

  const [scrollManagerRef, setScrollManagerRef] = useState(null);
  const [scrollUpdated, setScrollUpdated] = useState(0);

  /**
   * Handle container max width and height adjustments
   *
   * The page margin, padding, and border should be applied to the container and
   * reduce the max dimensions of the container element.
   *
   * Why is this? The container element determines the max dimensions that the
   * fields and PDF background can be within. The border, padding, and margin of
   * the page itself reduce that max dimension limit. Therefore we apply those
   * page properties to the container since that is what enforces those dimensions
   * for the page fields and PDF background.
   *
   * For instance if the container max width is 816px and the page has padding of
   * 4px (x2 for both sides) then the max width of the container should be reduced
   * to 804px. Then we can scale the PDF background and fields to fit within that
   * new 804px width limit.
   */
  const adjustedContainerWidth = getAdjustedDimension(containerWidth, padding, margin, borderWidth);
  const adjustedContainerHeight = getAdjustedDimension(containerHeight, padding, margin, borderWidth);

  /**
   * Why do we have duplicate arguments for width? With e-forms we only need to scale by width. We do not
   * need to worry about height because height is not restricted in e-form format. We pass width twice so
   * that we get the same percentage difference and always have the width adjustment percentage given to us.
   */
  const percentageDifference = calculatePagePercentageAdjustments(width, width, adjustedContainerWidth, adjustedContainerWidth);

  /**
   * Why -18 from the page width? This is to adjust the react-grid-layout away from
   * the scrollbar gutter width. This way when the scrollbar appears due to overflow
   * the field drag and resize icons wont be covered up by the scollbar.
   */
  const scrollbarGutterWidth = 18;
  const adjustedPageWidth = getPercentageAdjustedValue(width, percentageDifference) - scrollbarGutterWidth;
  const adjustedPageHeight = getPercentageAdjustedValue(height, percentageDifference);

  /**
   * We join width and color to a single border style because chrome displays
   * the border as a two tone color if you use border-color and border-width
   * indvidually.
   */
  const pageBorderWidth = borderWidth !== undefined ? `${borderWidth}px` : '0px';
  const pageBorderColor = borderColor || 'transparent';
  const pageBorderStyle = borderStyle || 'solid';
  const pageBorder = `${pageBorderWidth} ${pageBorderStyle} ${pageBorderColor}`;

  /**
   * Start Field Helpers
   */
  const FieldCreationDropLayerElement = mode === FieldModes.edit ? FieldCreationDropLayer : Fragment;

  const dropElementProps = {};

  if (mode === FieldModes.edit) {

    dropElementProps.pageWidth = width;
    dropElementProps.pageColumns = cols;
    dropElementProps.pageRowHeight = rowHeight;
    dropElementProps.onDrop = onFieldCreate;
    dropElementProps.mockTestDndMonitor = mockTestDndMonitor;

  }

  const getFields = useCallback(() => {

    return getPageFieldPositions({
      fields,
      mode,
      presentation,
    });

  }, [fields, presentation, mode]);

  const handleScroll = (e) => {
    const current = e.target.scrollTop;
    const difference = Math.abs(current - scrollUpdated);
    /**
     * When a user has scrolled in either direction greater than
     * 100px then trigger the lazyload field position visbility check
     */
    if (difference > 100) setScrollUpdated(e.target.scrollTop);
  };

  return (
    <PagePositionContext.Provider
      value={{
        top: scrollManagerRef ? scrollManagerRef.getBoundingClientRect().top : 0,
        bottom: adjustedPageHeight + (scrollManagerRef ? scrollManagerRef.getBoundingClientRect().top : 0),
        scrollTop: scrollManagerRef ? scrollManagerRef.scrollTop : 0
      }}
    >
      <StyledWrapper
        {...{ width: containerWidth }}
        onScroll={handleScroll}
        ref={(e) => {
          if (!e) return;
          if (!scrollManagerRef) setScrollManagerRef(e);
        }}
      >
        <StyledBorderWrapper
          {...{
            border: pageBorder,
            margin,
            padding,
            height: adjustedContainerHeight
          }}
        >
          <FieldCreationDropLayerElement
            {...dropElementProps}
          >
            <StyledPageFields>
              <GridLayout
                {...props}
                fields={getFields()}
                width={adjustedPageWidth}
                cols={cols}
                enableLazyLoadFields={enableLazyLoadFields}
                enableVirtualFields={enableVirtualFields}
              />
            </StyledPageFields>
          </FieldCreationDropLayerElement>
        </StyledBorderWrapper>
      </StyledWrapper>
    </PagePositionContext.Provider>
  );

};

/**
 * What is 2 used for?
 *
 * We do (* 2) to represent the number of sides of the page to take into account.
 *
 * For instance if you want to adjust the height of the page by subtracting the margin
 * from top and bottom of the page then you would use a multiple of 2.
 */
const getAdjustedDimension = (value, padding, margin, borderWidth) => {

  let pageValue = value;
  if (padding) pageValue -= (padding * 2);
  if (borderWidth) pageValue -= (borderWidth * 2);
  if (margin) pageValue -= (margin * 2);

  return pageValue;

};

export default FormPage;
